import { apiClient } from '@app/api';
import { useToast } from '@app/core/notifications';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { ModalController } from '@mi-front/ui';
import { Box, Button, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { AUDIOGRAM_TARGET_VARIANTS } from '../consts';
import { audiogramHelpers } from '../helpers';
import { AudiogramSocialExportDialog } from './AudiogramSocialExportDialog';
import { useFragment } from '../../../hooks/useFragment';

interface Props {
  platform: SocialPlatform;
}

export function AudiogramSocialExportStart({ platform }: Props) {
  const { fragment } = useFragment();
  const { $t } = useIntl();
  const { notifyInfo, notifyError } = useToast();
  const queryClient = useQueryClient();
  const completedVariants = audiogramHelpers.getCompletedVariants(fragment);
  const audiogramVariants = AUDIOGRAM_TARGET_VARIANTS.filter((v) =>
    completedVariants.some((c) => c.variant === v.value),
  );
  const [variant, setVariant] = useState<string>(audiogramVariants[0].value);

  const handleExport = async (data: any) => {
    try {
      await apiClient.fragments.audiogramSocialExport(fragment.id, platform.id, {
        ...data,
        variant,
      });
      notifyInfo(
        $t({
          id: 'audiogram.exportStarted',
          defaultMessage: 'Export started',
        }),
      );
      queryClient.invalidateQueries({ queryKey: ['fragment', fragment.id] });
    } catch (error) {
      notifyError(
        $t({
          id: 'audiogram.exportStartError',
          defaultMessage: 'Failed to start export, please contact support',
        }),
      );
      console.error(error);
    }
  };

  return (
    <Box display="flex" mt={2}>
      <FormControl variant="outlined" sx={{ mr: 1, width: '200px' }}>
        <InputLabel id="demo-simple-select-label">
          {$t({
            id: 'audiogram.format',
            defaultMessage: 'Formato',
          })}
        </InputLabel>
        <Select
          value={variant}
          label={$t({
            id: 'audiogram.format',
            defaultMessage: 'Formato',
          })}
          size="small"
          onChange={(e) => setVariant(e.target.value)}
        >
          {audiogramVariants.map((v) => (
            <MenuItem key={v.value} value={v.value}>
              {$t(v.message)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <ModalController
        trigger={(open) => (
          <Button
            size={'small'}
            color="inherit"
            variant="outlined"
            onClick={open}
            startIcon={platform.Icon()}
          >
            {$t({
              id: 'audiogram.exportButton',
              defaultMessage: 'Export',
            })}
          </Button>
        )}
        children={useCallback(
          ({ show, onHide }) => {
            return (
              <AudiogramSocialExportDialog
                platform={platform}
                variant={variant}
                show={show}
                onHide={onHide}
                onSubmit={async (data) => {
                  await handleExport(data);
                  onHide();
                }}
              />
            );
          },
          [variant, platform],
        )}
      />
    </Box>
  );
}
